import { BLUEPRINTS, CORES, EVOLUTIONS, UPGRADES, getCore } from '../data/content';
import {
  CORE_SKINS,
  SKILL_SKINS,
  getCoreSkin,
  getSkillSkin,
  type CoreSkinId,
  type SkillSkinId,
} from '../data/cosmetics';
import { GameRuntime } from '../game/GameRuntime';
import { Simulation, getEvolutionName, getSynergyName } from '../game/simulation/Simulation';
import type {
  CoreId,
  GameEvent,
  SimulationSnapshot,
  UpgradeOffer,
  UpgradeRarity,
} from '../game/simulation/types';
import { LocalAnalytics } from '../services/analytics';
import { AudioDirector } from '../services/audio';
import { CapacitorPlatformServices } from '../services/platform';
import {
  IndexedDbSaveRepository,
  CORE_MASTERY_THRESHOLDS,
  WORKSHOP_COSTS,
  coreMasteryLevel,
  createDefaultSave,
  isCoreUnlocked,
  profileXpRequired,
  toMetaModifiers,
  type MetaSaveV3,
  type PlayerSettings,
} from '../services/save';
import type { RuntimeFrame } from '../game/render/BattleScene';
import { t } from './i18n';

type AppScreen = 'loading' | 'home' | 'battle' | 'workshop' | 'cosmetics' | 'settings';
type RunMode = 'normal' | 'endgame';

export class App {
  private readonly saveRepository = new IndexedDbSaveRepository();
  private readonly platform = new CapacitorPlatformServices();
  private readonly analytics = new LocalAnalytics();
  private readonly audio = new AudioDirector();
  private readonly runtime: GameRuntime;
  private save: MetaSaveV3 = createDefaultSave();
  private simulation: Simulation | null = null;
  private snapshot: SimulationSnapshot | null = null;
  private screen: AppScreen = 'loading';
  private lastOverlaySignature = '';
  private resultPersisted = false;
  private resultSaveComplete = false;
  private debugSpeed = 1;
  private runMode: RunMode = 'normal';

  constructor(private readonly root: HTMLElement) {
    this.runtime = new GameRuntime((frame) => this.onFrame(frame));
  }

  async start(): Promise<void> {
    this.renderLoading();
    this.save = await this.saveRepository.load();
    this.runtime.setSettings(this.save.settings);
    this.applyCosmetics();
    this.audio.setMuted(this.save.settings.muted);
    this.bindLifecycle();
    this.showHome();
  }

  private onFrame({ snapshot, events }: RuntimeFrame): void {
    this.snapshot = snapshot;
    if (import.meta.env.DEV) {
      this.root.dataset.playerX = snapshot.player.x.toFixed(2);
      this.root.dataset.playerY = snapshot.player.y.toFixed(2);
      this.root.dataset.xpPickups = snapshot.xpPickups.length.toString();
      this.root.dataset.wave = snapshot.wave.toString();
      this.root.dataset.coreId = snapshot.coreId;
      this.root.dataset.maxedSkills = UPGRADES.filter(
        (upgrade) => snapshot.upgradeRanks[upgrade.id] === upgrade.maxRank,
      ).length.toString();
    }
    this.audio.handle(events);
    this.processEvents(events);
    const signature = [
      snapshot.phase,
      snapshot.upgradeOffers.map((offer) => `${offer.id}:${offer.nextRank}`).join(','),
      snapshot.shopOffers.map((offer) => `${offer.id}:${offer.cost}:${offer.affordable}`).join(','),
      snapshot.pendingEvolutionId ?? '',
    ].join('|');
    if (signature !== this.lastOverlaySignature && this.screen === 'battle') {
      this.lastOverlaySignature = signature;
      this.renderBattleOverlay();
    }
    if ((snapshot.phase === 'victory' || snapshot.phase === 'defeat') && !this.resultPersisted) {
      this.resultPersisted = true;
      if (this.runMode === 'endgame') {
        this.analytics.track('endgame_test_complete', {
          result: snapshot.phase,
          duration: snapshot.stats.elapsedTime,
          kills: snapshot.stats.kills,
        });
        this.resultSaveComplete = true;
        if (this.screen === 'battle') this.renderBattleOverlay();
      } else {
        void this.persistResult(snapshot);
      }
    }
  }

  private processEvents(events: readonly GameEvent[]): void {
    for (const event of events) {
      if (event.type === 'coreDamaged' && this.save.settings.haptics)
        void this.platform.impact('light');
      if (event.type === 'synergyUnlocked') {
        this.analytics.track('synergy_unlocked', { synergyId: event.payload.synergyId });
        if (this.save.settings.haptics) void this.platform.notification('success');
        this.toast(`SYNERGY: ${event.payload.name}`);
      }
      if (event.type === 'evolutionUnlocked') {
        this.analytics.track('evolution_triggered', { evolutionId: event.payload.evolutionId });
        if (this.save.settings.haptics) void this.platform.impact('heavy');
      }
      if (event.type === 'systemUpgradeActivated') {
        const complete = event.payload.rank === event.payload.maxRank;
        this.toast(
          `${complete ? 'SYSTEM MASTERED' : `TIER ${event.payload.tier} ONLINE`}: ${event.payload.name} ${event.payload.rank}/${event.payload.maxRank}`,
        );
      }
      if (event.type === 'coreAbilityActivated' && this.save.settings.haptics)
        void this.platform.impact('light');
      if (event.type === 'overdriveActivated') {
        this.toast('MOMENTUM OVERDRIVE · ATTACK SPEED SURGE');
        if (this.save.settings.haptics) void this.platform.impact('heavy');
      }
      if (event.type === 'bossArenaLocked') {
        this.toast('BOSS ARENA LOCKED · DEFEAT THE TARGET');
        if (this.save.settings.haptics) void this.platform.notification('warning');
      }
      if (event.type === 'bossArenaReleased') this.toast('ARENA BREACHED · ROUTE OPEN');
      if (
        event.type === 'xpCollected' &&
        (event.payload.tier === 'gold' || event.payload.tier === 'prismatic') &&
        this.save.settings.haptics
      )
        void this.platform.impact(event.payload.tier === 'prismatic' ? 'heavy' : 'medium');
    }
  }

  private renderLoading(): void {
    this.screen = 'loading';
    document.body.dataset.screen = 'loading';
    this.root.innerHTML = `
      <section class="screen center-screen panel-void">
        <div class="brand-mark" aria-hidden="true"><span></span></div>
        <h1 class="logo">NEOCORE</h1>
        <div class="loading-line"><i></i></div>
        <p class="eyebrow">INITIALIZING REACTOR</p>
      </section>`;
  }

  private showHome(): void {
    this.screen = 'home';
    this.simulation = null;
    this.snapshot = null;
    this.runtime.setSimulation(null);
    this.audio.stop();
    this.runMode = 'normal';
    document.body.dataset.screen = 'home';
    const selectedCore = getCore(this.save.selectedCoreId);
    const selectedSkin = getCoreSkin(this.save.cosmetics.coreSkinId);
    const selectedSkillSkin = getSkillSkin(this.save.cosmetics.skillSkinId);
    const mastery = coreMasteryLevel(this.save.coreMasteryXp[selectedCore.id]);
    const profileTarget = profileXpRequired(this.save.profileLevel);
    const profileProgress = Math.min(100, (this.save.profileXp / profileTarget) * 100);
    this.root.innerHTML = `
      <section class="screen home-screen">
        <header class="resource-header glass-panel">
          <div class="profile-level">
            <span class="resource-label">PROFILE LVL ${this.save.profileLevel}</span>
            <i><b style="width:${profileProgress}%"></b></i>
          </div>
          <strong class="coin-value"><i class="coin-dot"></i>${this.save.coreCoins.toLocaleString('en-US')}</strong>
        </header>
        <div class="home-brand">
          <p class="eyebrow">CORE DEFENSE PROTOCOL</p>
          <h1 class="logo">NEOCORE</h1>
        </div>
        <section class="core-loadout rarity-${selectedCore.rarity} skin-${selectedSkin.id}" style="--core-color:${this.colorHex(selectedSkin.primary)};--core-accent:${this.colorHex(selectedSkin.secondary)}">
          <div class="home-core" aria-hidden="true"><i></i><i></i><i></i></div>
          <div class="core-loadout-copy">
            <span>${this.rarityLabel(selectedCore.rarity)} · MASTERY ${mastery}</span>
            <h2>${selectedCore.name}</h2>
            <p>${selectedCore.subtitle}</p>
            <strong>${selectedCore.abilityName}</strong>
            <small>${selectedCore.abilityDescription}</small>
            <div class="equipped-skin"><b>${selectedSkin.name}</b><i></i><b>${selectedSkillSkin.name} FX</b></div>
          </div>
        </section>
        <div class="core-roster" aria-label="Core roster">
          ${CORES.map((core) => {
            const unlocked = isCoreUnlocked(core.id, this.save);
            const selected = core.id === selectedCore.id;
            return `<button class="core-chip rarity-${core.rarity} ${selected ? 'is-selected' : ''}" data-core="${core.id}" ${unlocked ? '' : 'disabled'} aria-label="${core.name}${unlocked ? '' : ` locked until profile level ${core.unlockLevel}`}">
              <i style="--chip-color:${this.colorHex(core.color)}"></i>
              <span>${core.name.replace(' Core', '')}</span>
              <em>${unlocked ? this.rarityLabel(core.rarity) : `LVL ${core.unlockLevel}`}</em>
            </button>`;
          }).join('')}
        </div>
        <nav class="home-actions" aria-label="Main menu">
          <button class="button button-primary button-play" data-action="play"><span>${t('play')}</span></button>
          <button class="button button-endgame" data-action="endgame">
            <span>${t('endgame')}</span>
            <small>SINGULARITY · MAX BUILD · WAVE 9</small>
          </button>
          <div class="button-grid">
            <button class="button button-secondary" data-action="workshop">${t('workshop')}</button>
            <button class="button button-secondary button-skins" data-action="cosmetics">SKIN VAULT</button>
            <button class="button button-secondary" data-action="settings">${t('settings')}</button>
          </div>
          <div class="blueprint-status ${this.hasBlueprint() ? 'is-unlocked' : ''}">
            <span>${this.hasBlueprint() ? '◆' : '◇'}</span>
            ${this.hasBlueprint() ? t('blueprintUnlocked') : t('blueprintLocked')}
          </div>
        </nav>
        <footer class="version-label">ASCENDANT SKINS · v0.4.0</footer>
      </section>`;
    this.root
      .querySelector('[data-action="play"]')
      ?.addEventListener('click', () => this.startRun());
    this.root
      .querySelector('[data-action="endgame"]')
      ?.addEventListener('click', () => this.startRun('endgame'));
    this.root
      .querySelector('[data-action="workshop"]')
      ?.addEventListener('click', () => this.showWorkshop());
    this.root
      .querySelector('[data-action="cosmetics"]')
      ?.addEventListener('click', () => this.showCosmetics());
    this.root
      .querySelector('[data-action="settings"]')
      ?.addEventListener('click', () => this.showSettings());
    this.root.querySelectorAll<HTMLElement>('[data-core]').forEach((element) => {
      element.addEventListener('click', async () => {
        const coreId = element.dataset.core as CoreId | undefined;
        if (!coreId || !isCoreUnlocked(coreId, this.save)) return;
        this.save = await this.saveRepository.selectCore(coreId);
        this.analytics.track('core_selected', { coreId });
        if (this.save.settings.haptics) void this.platform.impact('medium');
        this.showHome();
      });
    });
  }

  private startRun(mode: RunMode = 'normal'): void {
    const seed = (Date.now() ^ Math.floor(Math.random() * 0xffffffff)) >>> 0;
    this.runMode = mode;
    const meta =
      mode === 'endgame'
        ? {
            powerLevel: WORKSHOP_COSTS.length,
            integrityLevel: WORKSHOP_COSTS.length,
            insightLevel: WORKSHOP_COSTS.length,
            unlockedBlueprints: BLUEPRINTS.map((blueprint) => blueprint.id),
            selectedCoreId: 'singularity' as const,
            coreMasteryLevel: CORE_MASTERY_THRESHOLDS.length - 1,
          }
        : toMetaModifiers(this.save);
    this.simulation = Simulation.start(
      seed,
      meta,
      mode === 'endgame' ? 'endgame-test' : 'standard',
    );
    this.snapshot = this.simulation.getSnapshot();
    this.runtime.setSimulation(this.simulation);
    this.runtime.setCosmetics(this.save.cosmetics);
    this.runtime.setSpeed(1);
    this.debugSpeed = 1;
    this.resultPersisted = false;
    this.resultSaveComplete = false;
    this.lastOverlaySignature = '';
    this.screen = 'battle';
    document.body.dataset.screen = 'battle';
    this.analytics.track('run_start', { seed, mode });
    void this.audio.start();
    this.renderBattleOverlay();
    if (mode === 'endgame') {
      this.toast('ENDGAME TEST · ALL SYSTEMS MAXED · WAVE 9');
      if (this.save.settings.haptics) void this.platform.impact('heavy');
    }
  }

  private renderBattleOverlay(): void {
    if (!this.snapshot) {
      this.root.innerHTML = `<section class="screen battle-ui"><button class="icon-button pause-button" data-action="pause" aria-label="${t('pause')}">Ⅱ</button></section>`;
      this.bindBattleControls();
      return;
    }
    const snapshot = this.snapshot;
    let modal = '';
    if (snapshot.phase === 'level-up') modal = this.upgradeModal(snapshot);
    if (snapshot.phase === 'intermission') modal = this.shopModal(snapshot);
    if (snapshot.phase === 'evolution') modal = this.evolutionModal(snapshot);
    if (snapshot.phase === 'paused') modal = this.pauseModal();
    if (snapshot.phase === 'victory' || snapshot.phase === 'defeat')
      modal = this.resultModal(snapshot);

    this.root.innerHTML = `
      <section class="screen battle-ui">
        <button class="icon-button pause-button" data-action="pause" aria-label="${t('pause')}" ${snapshot.phase !== 'combat' ? 'hidden' : ''}>Ⅱ</button>
        ${this.runMode === 'endgame' ? '<div class="endgame-badge"><strong>ENDGAME TEST</strong><span>MAX BUILD · NO SAVE PROGRESS</span></div>' : ''}
        ${snapshot.stats.elapsedTime < 2 && snapshot.phase === 'combat' ? '<div class="movement-hint"><span>DRAG TO MOVE</span><small>CORE-LOCKED CAMERA · AUTO-FIRE ONLINE</small></div>' : ''}
        ${this.combatLoadout(snapshot)}
        ${modal}
        ${import.meta.env.DEV ? this.debugMenu() : ''}
      </section>`;
    this.bindBattleControls();
  }

  private upgradeModal(snapshot: SimulationSnapshot): string {
    const offers = snapshot.upgradeOffers
      .map((offer) => {
        const currentRank = snapshot.upgradeRanks[offer.id] ?? 0;
        return { ...offer, currentRank, nextRank: currentRank + 1 };
      })
      .filter((offer) => offer.currentRank < offer.maxRank && offer.nextRank <= offer.maxRank);
    return `
      <div class="modal-backdrop">
        <section class="choice-modal ascendancy-modal" role="dialog" aria-labelledby="upgrade-title">
          <div class="ascension-crown" aria-hidden="true"><i></i><b>✦</b><i></i></div>
          <p class="eyebrow">SYSTEM DRAFT · LEVEL ${snapshot.level}</p>
          <h2 id="upgrade-title">${t('upgradeTitle')}</h2>
          <p>Choose your ascension. Every rank changes the battlefield.</p>
          <div class="card-stack skill-card-stack">
            ${offers
              .map(
                (offer) => `
              <button class="upgrade-card rarity-${offer.rarity} family-${offer.family} skill-skin-${this.save.cosmetics.skillSkinId} rank-${offer.nextRank} ${offer.nextRank === offer.maxRank ? 'is-mastery' : ''}" data-upgrade="${offer.id}" data-rank="${offer.nextRank}">
                <span class="card-family">${offer.family} · SYSTEM TIER ${offer.tier}</span>
                <span class="skill-portrait" aria-hidden="true"><i></i><b>${this.skillGlyph(offer.id)}</b><em></em></span>
                <strong>${offer.name}</strong>
                <em>${this.rarityLabel(offer.rarity)} · ${this.offerRankLabel(offer)}</em>
                <p>${offer.description}</p>
                <span class="upgrade-path">${offer.path}</span>
                <span class="system-ranks" aria-label="Current rank ${offer.currentRank} of ${offer.maxRank}; selecting advances to ${offer.nextRank}">${Array.from({ length: offer.maxRank }, (_, index) => `<i class="${index < offer.currentRank ? 'active' : index === offer.currentRank ? 'next' : ''}">${index < offer.currentRank ? '★' : index === offer.currentRank ? '✦' : '☆'}</i>`).join('')}</span>
              </button>`,
              )
              .join('')}
          </div>
          <button class="button button-secondary reroll-button" data-action="reroll" ${snapshot.rerollsRemaining <= 0 ? 'disabled' : ''}>
            ${t('reroll')} · ${snapshot.rerollsRemaining}
          </button>
        </section>
      </div>`;
  }

  private shopModal(snapshot: SimulationSnapshot): string {
    return `
      <div class="modal-backdrop">
        <section class="choice-modal" role="dialog" aria-labelledby="shop-title">
          <p class="eyebrow">WAVE ${snapshot.wave}/10</p>
          <h2 id="shop-title">${t('shopTitle')}</h2>
          <p>${t('shopSubtitle')}</p>
          <div class="cash-pill">$ ${snapshot.cash.toLocaleString('en-US')}</div>
          <div class="card-stack shop-stack">
            ${snapshot.shopOffers
              .map(
                (offer) => `
              <button class="upgrade-card shop-card" data-shop="${offer.id}" ${offer.affordable ? '' : 'disabled'}>
                <span class="card-family">COMBAT STAT</span>
                <strong>${offer.name}</strong>
                <p>${offer.description}</p>
                <em>$ ${offer.cost}</em>
              </button>`,
              )
              .join('')}
          </div>
          <button class="button button-ghost" data-action="skip-shop">${t('skip')}</button>
        </section>
      </div>`;
  }

  private evolutionModal(snapshot: SimulationSnapshot): string {
    const id = snapshot.pendingEvolutionId ?? '';
    const definition = EVOLUTIONS.find((item) => item.id === id);
    return `
      <div class="modal-backdrop evolution-backdrop">
        <section class="evolution-modal" role="dialog" aria-labelledby="evolution-title">
          <div class="evolution-sigil" aria-hidden="true"><i></i><i></i><i></i></div>
          <p class="eyebrow">${t('evolutionTitle')}</p>
          <h2 id="evolution-title">${definition?.name ?? getEvolutionName(id)}</h2>
          <p>${definition?.description ?? ''}</p>
          <button class="button button-evolution" data-action="ack-evolution">${t('continue')}</button>
        </section>
      </div>`;
  }

  private pauseModal(): string {
    return `
      <div class="modal-backdrop">
        <section class="choice-modal compact-modal" role="dialog" aria-labelledby="pause-title">
          <p class="eyebrow">SYSTEM HOLD</p>
          <h2 id="pause-title">${t('pause')}</h2>
          <button class="button button-primary" data-action="resume">${t('resume')}</button>
          <button class="button button-secondary" data-action="battle-settings">${t('settings')}</button>
          <button class="button button-danger" data-action="abandon">${t('abandon')}</button>
        </section>
      </div>`;
  }

  private resultModal(snapshot: SimulationSnapshot): string {
    const victory = snapshot.phase === 'victory';
    const endgameTest = this.runMode === 'endgame';
    return `
      <div class="modal-backdrop result-backdrop">
        <section class="result-modal" role="dialog" aria-labelledby="result-title">
          <p class="eyebrow">${endgameTest ? 'ENDGAME SIMULATION COMPLETE' : 'RUN COMPLETE'}</p>
          <h2 id="result-title" class="${victory ? 'victory-text' : 'danger-text'}">${victory ? t('victoryTitle') : t('defeatTitle')}</h2>
          <div class="result-grid">
            <div><span>WAVE</span><strong>${snapshot.wave}</strong></div>
            <div><span>TIME</span><strong>${this.formatTime(snapshot.stats.elapsedTime)}</strong></div>
            <div><span>ENEMIES</span><strong>${snapshot.stats.kills.toLocaleString('en-US')}</strong></div>
            <div><span>DAMAGE</span><strong>${Math.round(snapshot.stats.damageDealt).toLocaleString('en-US')}</strong></div>
            <div><span>MAX COMBO</span><strong>×${snapshot.stats.maxCombo}</strong></div>
            <div><span>CORE LEVEL</span><strong>${snapshot.level}</strong></div>
          </div>
          ${endgameTest ? '<div class="endgame-result-note">TEST MODE · SAVE DATA AND REWARDS UNCHANGED</div>' : `<div class="reward-panel"><span>${t('reward')}</span><strong><i class="coin-dot"></i>${snapshot.resultReward}</strong></div><div class="progression-reward"><span>PROFILE XP +${snapshot.resultProfileXp}</span><span>${getCore(snapshot.coreId).name.toUpperCase()} XP +${snapshot.resultCoreXp}</span></div>${snapshot.blueprintReward ? `<div class="blueprint-reveal">◆ ${t('blueprintAcquired')}</div>` : ''}`}
          <button class="button button-primary" data-action="play-again" ${this.resultSaveComplete ? '' : 'disabled'}>${this.resultSaveComplete ? (endgameTest ? 'RESTART ENDGAME' : t('playAgain')) : 'SAVING...'}</button>
          <div class="button-grid">
            <button class="button button-secondary" data-action="result-workshop" ${this.resultSaveComplete ? '' : 'disabled'}>${t('workshop')}</button>
            <button class="button button-secondary" data-action="home" ${this.resultSaveComplete ? '' : 'disabled'}>${t('home')}</button>
          </div>
        </section>
      </div>`;
  }

  private bindBattleControls(): void {
    this.root
      .querySelector('[data-action="pause"]')
      ?.addEventListener('click', () => this.runtime.command({ type: 'pause' }));
    this.root
      .querySelector('[data-action="resume"]')
      ?.addEventListener('click', () => this.runtime.command({ type: 'resume' }));
    this.root.querySelectorAll<HTMLElement>('[data-upgrade]').forEach((element) => {
      element.addEventListener('click', () => {
        const upgradeId = element.dataset.upgrade;
        if (!upgradeId || !this.snapshot || element.dataset.locked === 'true') return;
        const offer = this.snapshot.upgradeOffers.find((candidate) => candidate.id === upgradeId);
        const currentRank = this.snapshot.upgradeRanks[upgradeId] ?? 0;
        const expectedRank = currentRank + 1;
        if (!offer || currentRank >= offer.maxRank || expectedRank > offer.maxRank) {
          element.setAttribute('disabled', '');
          element.dataset.locked = 'true';
          return;
        }
        this.root.querySelectorAll<HTMLButtonElement>('[data-upgrade]').forEach((button) => {
          button.disabled = true;
          button.dataset.locked = 'true';
        });
        element.classList.add('is-activating');
        this.analytics.track('upgrade_selected', { upgradeId, rank: expectedRank });
        if (this.save.settings.haptics)
          void this.platform.impact(expectedRank >= offer.maxRank ? 'heavy' : 'medium');
        window.setTimeout(
          () =>
            this.runtime.command({
              type: 'select-upgrade',
              upgradeId,
              expectedRank,
            }),
          150 + expectedRank * 70,
        );
      });
    });
    this.root.querySelector('[data-action="reroll"]')?.addEventListener('click', () => {
      this.analytics.track('reroll_used', {});
      this.runtime.command({ type: 'reroll-upgrades' });
    });
    this.root.querySelectorAll<HTMLElement>('[data-shop]').forEach((element) => {
      element.addEventListener('click', () => {
        const shopId = element.dataset.shop as
          | 'damage'
          | 'attack-speed'
          | 'max-health'
          | 'armor'
          | 'regeneration'
          | 'critical'
          | undefined;
        if (shopId) this.runtime.command({ type: 'buy-shop', shopId });
      });
    });
    this.root
      .querySelector('[data-action="skip-shop"]')
      ?.addEventListener('click', () => this.runtime.command({ type: 'buy-shop', shopId: null }));
    this.root
      .querySelector('[data-action="ack-evolution"]')
      ?.addEventListener('click', () => this.runtime.command({ type: 'acknowledge-evolution' }));
    this.root.querySelector('[data-action="abandon"]')?.addEventListener('click', () => {
      if (window.confirm('Abandon this run? Progress from the current run will be lost.'))
        this.showHome();
    });
    this.root
      .querySelector('[data-action="battle-settings"]')
      ?.addEventListener('click', () => this.showSettings(true));
    this.root
      .querySelector('[data-action="play-again"]')
      ?.addEventListener('click', () => this.startRun(this.runMode));
    this.root
      .querySelector('[data-action="result-workshop"]')
      ?.addEventListener('click', () => this.showWorkshop());
    this.root
      .querySelector('[data-action="home"]')
      ?.addEventListener('click', () => this.showHome());
    this.bindDebugControls();
  }

  private async persistResult(snapshot: SimulationSnapshot): Promise<void> {
    this.analytics.track('run_end', {
      result: snapshot.phase === 'victory' ? 'victory' : 'defeat',
      wave: snapshot.wave,
      duration: snapshot.stats.elapsedTime,
    });
    this.save = await this.saveRepository.applyRunResult({
      reward: snapshot.resultReward,
      profileXp: snapshot.resultProfileXp,
      coreXp: snapshot.resultCoreXp,
      coreId: snapshot.coreId,
      victory: snapshot.phase === 'victory',
      wave: snapshot.wave,
      kills: snapshot.stats.kills,
      damage: snapshot.stats.damageDealt,
      maxCombo: snapshot.stats.maxCombo,
      blueprintId: snapshot.blueprintReward,
    });
    this.resultSaveComplete = true;
    if (this.screen === 'battle') this.renderBattleOverlay();
  }

  private showWorkshop(): void {
    this.screen = 'workshop';
    this.runtime.setSimulation(null);
    this.audio.stop();
    document.body.dataset.screen = 'workshop';
    const branches = [
      { id: 'power', name: t('power'), description: t('powerDescription') },
      { id: 'integrity', name: t('integrity'), description: t('integrityDescription') },
      { id: 'insight', name: t('insight'), description: t('insightDescription') },
    ] as const;
    this.root.innerHTML = `
      <section class="screen menu-screen">
        <header class="menu-header">
          <button class="icon-button back-button" data-action="back">←</button>
          <div><p class="eyebrow">PERMANENT PROGRESSION</p><h1>${t('workshop')}</h1></div>
          <strong class="coin-value"><i class="coin-dot"></i>${this.save.coreCoins}</strong>
        </header>
        <div class="workshop-core" aria-hidden="true"><i></i><i></i><i></i></div>
        <div class="branch-list">
          ${branches
            .map((branch) => {
              const level = this.save.workshop[branch.id];
              const maxed = level >= WORKSHOP_COSTS.length;
              const cost = maxed ? 0 : WORKSHOP_COSTS[level]!;
              return `
              <article class="branch-card">
                <div class="branch-copy"><span>${branch.name}</span><p>${branch.description}</p></div>
                <div class="level-pips">${Array.from({ length: 5 }, (_, index) => `<i class="${index < level ? 'active' : ''}"></i>`).join('')}</div>
                <button class="button button-small" data-branch="${branch.id}" ${maxed || this.save.coreCoins < cost ? 'disabled' : ''}>
                  ${maxed ? t('max') : `${t('purchase')} · ${cost}`}
                </button>
              </article>`;
            })
            .join('')}
        </div>
        <div class="blueprint-card ${this.hasBlueprint() ? 'is-unlocked' : ''}">
          <span class="blueprint-icon">${this.hasBlueprint() ? '◆' : '◇'}</span>
          <div><strong>${BLUEPRINTS[0].name}</strong><p>${this.hasBlueprint() ? BLUEPRINTS[0].description : 'Defeat The Devourer to reveal this blueprint.'}</p></div>
        </div>
      </section>`;
    this.root
      .querySelector('[data-action="back"]')
      ?.addEventListener('click', () => this.showHome());
    this.root.querySelectorAll<HTMLElement>('[data-branch]').forEach((element) => {
      element.addEventListener('click', async () => {
        const branch = element.dataset.branch as 'power' | 'integrity' | 'insight' | undefined;
        if (!branch) return;
        this.save = await this.saveRepository.purchaseWorkshop(branch);
        this.analytics.track('workshop_purchase', { branch, level: this.save.workshop[branch] });
        if (this.save.settings.haptics) void this.platform.impact('medium');
        this.showWorkshop();
      });
    });
  }

  private showCosmetics(): void {
    this.screen = 'cosmetics';
    this.runtime.setSimulation(null);
    this.audio.stop();
    document.body.dataset.screen = 'cosmetics';
    const coreSkin = getCoreSkin(this.save.cosmetics.coreSkinId);
    const skillSkin = getSkillSkin(this.save.cosmetics.skillSkinId);
    this.root.innerHTML = `
      <section class="screen menu-screen cosmetics-screen">
        <header class="menu-header vault-header">
          <button class="icon-button back-button" data-action="cosmetics-back" aria-label="Back">←</button>
          <div><p class="eyebrow">COSMETIC LOADOUT</p><h1>SKIN VAULT</h1></div>
          <span class="vault-rarity">◆ FREE</span>
        </header>
        <section class="vault-hero" style="--vault-primary:${this.colorHex(coreSkin.primary)};--vault-secondary:${this.colorHex(coreSkin.secondary)}">
          <div class="vault-energy" aria-hidden="true"><i></i><i></i><b></b></div>
          <div class="vault-hero-copy">
            <span>EQUIPPED CHASSIS</span>
            <h2>${coreSkin.name}</h2>
            <p>${coreSkin.lore}</p>
            <strong>${skillSkin.name} · FX LOADOUT</strong>
          </div>
        </section>
        <div class="vault-section-title"><span>CORE SKINS</span><em>3 / 3 UNLOCKED</em></div>
        <div class="skin-carousel" aria-label="Core skins">
          ${CORE_SKINS.map(
            (
              skin,
            ) => `<button class="skin-card core-skin-card skin-${skin.id} ${skin.id === coreSkin.id ? 'is-equipped' : ''}" data-core-skin="${skin.id}" style="--skin-color:${this.colorHex(skin.primary)};--skin-accent:${this.colorHex(skin.secondary)}" aria-label="${skin.name}">
              <span class="skin-orb"><i></i><b></b></span>
              <strong>${skin.name}</strong>
              <small>${skin.subtitle}</small>
              <em>${skin.id === coreSkin.id ? 'EQUIPPED' : 'EQUIP'}</em>
            </button>`,
          ).join('')}
        </div>
        <div class="vault-section-title"><span>SKILL FX SKINS</span><em>COSMETIC ONLY</em></div>
        <div class="fx-skin-list" aria-label="Skill effect skins">
          ${SKILL_SKINS.map(
            (
              skin,
            ) => `<button class="fx-skin-card skill-skin-${skin.id} ${skin.id === skillSkin.id ? 'is-equipped' : ''}" data-skill-skin="${skin.id}" aria-label="${skin.name}">
              <span class="fx-sample" aria-hidden="true"><i></i><i></i><i></i></span>
              <span><strong>${skin.name}</strong><small>${skin.description}</small></span>
              <em>${skin.id === skillSkin.id ? 'EQUIPPED' : 'EQUIP'}</em>
            </button>`,
          ).join('')}
        </div>
        <div class="cosmetic-note">◇ VISUAL LOADOUT ONLY · GAMEPLAY STATS UNCHANGED</div>
      </section>`;
    this.root
      .querySelector('[data-action="cosmetics-back"]')
      ?.addEventListener('click', () => this.showHome());
    this.root.querySelectorAll<HTMLElement>('[data-core-skin]').forEach((element) => {
      element.addEventListener('click', async () => {
        const coreSkinId = element.dataset.coreSkin as CoreSkinId | undefined;
        if (!coreSkinId || coreSkinId === this.save.cosmetics.coreSkinId) return;
        this.save = await this.saveRepository.updateCosmetics({ coreSkinId });
        this.applyCosmetics();
        this.analytics.track('cosmetic_selected', { type: 'core', id: coreSkinId });
        if (this.save.settings.haptics) void this.platform.impact('heavy');
        this.showCosmetics();
      });
    });
    this.root.querySelectorAll<HTMLElement>('[data-skill-skin]').forEach((element) => {
      element.addEventListener('click', async () => {
        const skillSkinId = element.dataset.skillSkin as SkillSkinId | undefined;
        if (!skillSkinId || skillSkinId === this.save.cosmetics.skillSkinId) return;
        this.save = await this.saveRepository.updateCosmetics({ skillSkinId });
        this.applyCosmetics();
        this.analytics.track('cosmetic_selected', { type: 'skill', id: skillSkinId });
        if (this.save.settings.haptics) void this.platform.impact('medium');
        this.showCosmetics();
      });
    });
  }

  private showSettings(fromBattle = false): void {
    const returnToBattle = fromBattle && this.snapshot?.phase === 'paused';
    this.screen = 'settings';
    document.body.dataset.screen = 'settings';
    const settings = this.save.settings;
    this.root.innerHTML = `
      <section class="screen menu-screen settings-screen">
        <header class="menu-header">
          <button class="icon-button back-button" data-action="settings-back">←</button>
          <div><p class="eyebrow">SYSTEM CONFIGURATION</p><h1>${t('settings')}</h1></div>
        </header>
        <form class="settings-list">
          <label><span>${t('graphics')}</span><select name="graphicsQuality">${['low', 'medium', 'high', 'ultra'].map((value) => `<option value="${value}" ${settings.graphicsQuality === value ? 'selected' : ''}>${value.toUpperCase()}</option>`).join('')}</select></label>
          ${this.toggle('damageNumbers', t('damageNumbers'), settings.damageNumbers)}
          ${this.toggle('reducedShake', t('reducedShake'), settings.reducedShake)}
          ${this.toggle('reducedFlashes', t('reducedFlashes'), settings.reducedFlashes)}
          ${this.toggle('haptics', t('haptics'), settings.haptics)}
          ${this.toggle('muted', t('muted'), settings.muted)}
          <label><span>${t('textScale')}</span><select name="textScale">${[1, 1.1, 1.2].map((value) => `<option value="${value}" ${settings.textScale === value ? 'selected' : ''}>${Math.round(value * 100)}%</option>`).join('')}</select></label>
        </form>
        <div class="settings-note">Gameplay remains fully available offline. Online services are not included in v0.4.</div>
      </section>`;
    this.root.querySelector('[data-action="settings-back"]')?.addEventListener('click', () => {
      if (returnToBattle) {
        this.screen = 'battle';
        document.body.dataset.screen = 'battle';
        this.renderBattleOverlay();
      } else this.showHome();
    });
    this.root.querySelector('form')?.addEventListener('change', async (event) => {
      const input = event.target as HTMLInputElement | HTMLSelectElement;
      let value: string | boolean | number =
        input instanceof HTMLInputElement && input.type === 'checkbox'
          ? input.checked
          : input.value;
      if (input.name === 'textScale') value = Number(value);
      this.save = await this.saveRepository.updateSettings({
        [input.name]: value,
      } as Partial<PlayerSettings>);
      this.runtime.setSettings(this.save.settings);
      this.audio.setMuted(this.save.settings.muted);
      document.documentElement.style.setProperty(
        '--text-scale',
        this.save.settings.textScale.toString(),
      );
    });
  }

  private toggle(name: keyof PlayerSettings, label: string, checked: boolean): string {
    return `<label class="toggle-row"><span>${label}</span><input type="checkbox" name="${name}" ${checked ? 'checked' : ''}/><i></i></label>`;
  }

  private debugMenu(): string {
    return `
      <details class="debug-menu">
        <summary>${t('debug')}</summary>
        <button data-debug="xp">+XP</button>
        <button data-debug="wave">NEXT WAVE</button>
        <button data-debug="damage">DAMAGE CORE</button>
        <button data-debug="max-skills">MAX ALL SKILLS</button>
        <button data-debug="evolution">EVOLVE</button>
        <button data-debug="stress">STRESS</button>
        <button data-debug="speed">SPEED ×${this.debugSpeed === 1 ? 5 : 1}</button>
        <button data-debug="analytics">EXPORT LOG</button>
      </details>`;
  }

  private bindDebugControls(): void {
    this.root
      .querySelector('[data-debug="xp"]')
      ?.addEventListener('click', () =>
        this.runtime.command({ type: 'debug-add-xp', amount: 120 }),
      );
    this.root
      .querySelector('[data-debug="wave"]')
      ?.addEventListener('click', () => this.runtime.command({ type: 'debug-next-wave' }));
    this.root
      .querySelector('[data-debug="damage"]')
      ?.addEventListener('click', () =>
        this.runtime.command({ type: 'debug-damage-core', amount: 50 }),
      );
    this.root.querySelector('[data-debug="max-skills"]')?.addEventListener('click', () => {
      this.runtime.command({ type: 'debug-max-skills' });
      this.toast('ALL SKILLS · MAX RANK');
      if (this.save.settings.haptics) void this.platform.impact('heavy');
    });
    this.root
      .querySelector('[data-debug="evolution"]')
      ?.addEventListener('click', () => this.runtime.command({ type: 'debug-force-evolution' }));
    this.root
      .querySelector('[data-debug="stress"]')
      ?.addEventListener('click', () => this.runtime.command({ type: 'debug-stress' }));
    this.root.querySelector('[data-debug="speed"]')?.addEventListener('click', () => {
      this.debugSpeed = this.debugSpeed === 1 ? 5 : 1;
      this.runtime.setSpeed(this.debugSpeed);
      this.renderBattleOverlay();
    });
    this.root.querySelector('[data-debug="analytics"]')?.addEventListener('click', () => {
      const url = URL.createObjectURL(
        new Blob([this.analytics.export()], { type: 'application/json' }),
      );
      const link = document.createElement('a');
      link.href = url;
      link.download = `neocore-playtest-${Date.now()}.json`;
      link.click();
      URL.revokeObjectURL(url);
    });
  }

  private bindLifecycle(): void {
    document.addEventListener('visibilitychange', () => {
      if (document.hidden && this.screen === 'battle' && this.snapshot?.phase === 'combat') {
        this.runtime.command({ type: 'pause' });
      }
    });
  }

  private toast(message: string): void {
    const toast = document.createElement('div');
    toast.className = 'toast';
    toast.textContent = message;
    this.root.append(toast);
    window.setTimeout(() => toast.remove(), 2200);
  }

  private hasBlueprint(): boolean {
    return this.save.unlockedBlueprints.includes('gravity_matrix');
  }

  private applyCosmetics(): void {
    document.body.dataset.coreSkin = this.save.cosmetics.coreSkinId;
    document.body.dataset.skillSkin = this.save.cosmetics.skillSkinId;
    this.runtime.setCosmetics(this.save.cosmetics);
  }

  private combatLoadout(snapshot: SimulationSnapshot): string {
    if (snapshot.phase === 'victory' || snapshot.phase === 'defeat') return '';
    const labels = { kinetic: 'K', tesla: 'T', cryo: 'C' } as const;
    return `<div class="combat-loadout" aria-label="Active systems">
      ${snapshot.weapons
        .map(
          (
            weapon,
          ) => `<span class="combat-system family-${weapon.family} ${weapon.enabled ? 'is-online' : 'is-locked'}" title="${weapon.family}">
            <b>${labels[weapon.family]}</b><i style="--system-level:${Math.max(1, weapon.visualLevel)}"></i><em>${weapon.enabled ? `LV.${weapon.visualLevel}` : 'LOCK'}</em>
          </span>`,
        )
        .join('')}
      ${snapshot.synergies.length > 0 ? `<strong class="synergy-count">◆ ${snapshot.synergies.length}</strong>` : ''}
    </div>`;
  }

  private skillGlyph(id: string): string {
    const glyphs: Record<string, string> = {
      heavy_rounds: '⬢',
      piercing_ammo: '➹',
      ricochet: '↯',
      explosive_shells: '✹',
      gatling_mode: '⟲',
      tesla_arc: 'ϟ',
      chain_lightning: '⌁',
      static_charge: '⊕',
      conductive_target: '⌖',
      overcharge: '⚡',
      cryo_emitter: '❄',
      cryo_field: '◉',
      frostbite: '✣',
      deep_freeze: '❅',
      shatter: '✧',
      gravity_well: '●',
      critical_matrix: '◇',
      resonant_reactor: '◎',
      repair_nanites: '✚',
      salvage_protocol: '◆',
    };
    return glyphs[id] ?? '✦';
  }

  private rarityLabel(rarity: UpgradeRarity): string {
    return rarity.toUpperCase();
  }

  private offerRankLabel(offer: UpgradeOffer): string {
    if (offer.currentRank === 0 && offer.maxRank === 1) return 'INSTALL → MASTERED';
    if (offer.nextRank === offer.maxRank) return `${offer.currentRank}/${offer.maxRank} → MAX`;
    return `${offer.currentRank}/${offer.maxRank} → ${offer.nextRank}/${offer.maxRank}`;
  }

  private colorHex(color: number): string {
    return `#${color.toString(16).padStart(6, '0')}`;
  }

  private formatTime(seconds: number): string {
    const minutes = Math.floor(seconds / 60);
    const remaining = Math.floor(seconds % 60);
    return `${minutes}:${remaining.toString().padStart(2, '0')}`;
  }
}

export function describeBuild(snapshot: SimulationSnapshot): string[] {
  return [
    ...snapshot.synergies.map(getSynergyName),
    ...(snapshot.evolutionId ? [getEvolutionName(snapshot.evolutionId)] : []),
  ];
}
